import React, { useEffect, useMemo, useState } from 'react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';
import { ModalShell, ModalHeader } from '../ui/ModalFrame';
import { MODAL_LAYER } from '../../constants/modalLayers';
import { MODAL_DIALOG_ACTIONS_END } from '../../constants/layoutSpacing';
import { cn } from '../../utils/cn';
import {
  DOCUMENT_SEARCH_MAX_RANGE_DAYS,
  DOCUMENT_STATUS_FILTER_OPTIONS,
  DOCUMENT_TYPE_FILTER_OPTIONS,
  defaultAdvancedSearchDateRange,
  validateAdvancedSearchForm,
} from '../../utils/documentAdvancedSearch';

const AdvancedDocumentSearchModal = ({
  open,
  onClose,
  onSearch,
  searching = false,
  lockSource = null,
  hideTypeFilter = false,
}) => {
  const defaults = useMemo(() => defaultAdvancedSearchDateRange(), []);

  const [title, setTitle] = useState('');
  const [status, setStatus] = useState('');
  const [type, setType] = useState('');
  const [dateFrom, setDateFrom] = useState(defaults.dateFrom);
  const [dateTo, setDateTo] = useState(defaults.dateTo);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    setErrors({});
    const range = defaultAdvancedSearchDateRange();
    setDateFrom(range.dateFrom);
    setDateTo(range.dateTo);
    if (lockSource === 'upload') {
      setType('uploaded_signature');
    }
  }, [open, lockSource]);

  if (!open) return null;

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = validateAdvancedSearchForm({
      title,
      status,
      type: lockSource === 'upload' ? 'uploaded_signature' : type,
      dateFrom,
      dateTo,
    });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    onSearch?.({
      title: title.trim(),
      status: status.trim(),
      type: lockSource === 'upload' ? 'uploaded_signature' : type.trim(),
      dateFrom: dateFrom.trim(),
      dateTo: dateTo.trim(),
      source: lockSource || 'all',
    });
  };

  return (
    <ModalShell
      zClass={MODAL_LAYER.standard}
      backdropClassName="bg-black/50"
      panelClassName="bg-card rounded-xl max-w-xl w-full shadow-elevation-lg border border-border flex flex-col max-h-[min(92vh,720px)]"
    >
      <ModalHeader title="Busca avançada" onClose={onClose} />
      <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1 overflow-hidden">
        <div className="px-5 py-4 space-y-4 overflow-y-auto min-h-[420px] flex-1">
          <p className="text-sm text-muted-foreground">
            Consulta no servidor por até {DOCUMENT_SEARCH_MAX_RANGE_DAYS} dias. Informe
            pelo menos um filtro.
          </p>

          {errors.form ? (
            <p className="text-sm text-destructive">{errors.form}</p>
          ) : null}

          <Input
            label="Nome do documento"
            placeholder="Parte do título…"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            error={errors.title}
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">
                Data início
              </label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                error={errors.dateFrom}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">
                Data fim
              </label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                error={errors.dateTo}
              />
            </div>
          </div>

          <Select
            options={DOCUMENT_STATUS_FILTER_OPTIONS}
            value={status}
            onChange={setStatus}
            placeholder="Status"
            portaled
            searchable
            optionsMaxHeightClass="max-h-72"
          />

          {!hideTypeFilter && lockSource !== 'upload' ? (
            <Select
              options={DOCUMENT_TYPE_FILTER_OPTIONS}
              value={type}
              onChange={setType}
              placeholder="Tipo"
              portaled
              searchable
              optionsMaxHeightClass="max-h-72"
            />
          ) : null}

          {lockSource === 'upload' ? (
            <p className="text-xs text-muted-foreground rounded-lg border border-border bg-muted/30 px-3 py-2">
              Busca restrita a PDFs enviados por upload.
            </p>
          ) : null}
        </div>

        <div className={cn(MODAL_DIALOG_ACTIONS_END, 'px-5 py-4 border-t border-border')}>
          <Button type="button" variant="outline" onClick={onClose} disabled={searching}>
            Cancelar
          </Button>
          <Button type="submit" disabled={searching}>
            {searching ? 'Buscando…' : 'Buscar'}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
};

export default AdvancedDocumentSearchModal;
